import React, { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";

const ProtectedRoute = ({ children, requiredRole }) => {
    const navigate = useNavigate();
    const { isLoggedIn, authState, userData, checkAuth } = useAuth();
    const location = useLocation();

    useEffect(() => {
        // Check authentication status when component mounts
        checkAuth();
    }, []);

    // Show loading state while checking authentication
    if (authState.isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Verifying your session...</p>
            </div>
        );
    }

    // Handle authentication errors
    if (authState.error) {
        return (
            <div className="error-container">
                <p>{authState.error}</p>
                <button onClick={() => navigate("/login")}>Return to Login</button>
            </div>
        );
    }

    // Only redirect if not logged in and no user data exists
    if (!isLoggedIn && !userData) {
        // Store the attempted URL to redirect back after login
        const returnTo = location.pathname;
        return <Navigate to="/login" state={{ from: returnTo }} replace />;
    }

    // If we have user data but not logged in, try to restore the session
    if (!isLoggedIn && userData) {
        checkAuth();
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Restoring your session...</p>
            </div>
        );
    }

    // Check if user has the required role
    if (requiredRole) {
        const normalizedRequired = Array.isArray(requiredRole)
            ? requiredRole.map((role) => role.toString().toLowerCase().trim())
            : [requiredRole.toString().toLowerCase().trim()];

        const derivedRole = (userData?.role || userData?.user_type || "")
            .toString()
            .toLowerCase()
            .trim();

        if (!normalizedRequired.includes(derivedRole)) {
            return <Navigate to="/unauthorized" replace />;
        }
    }

    // If we're logged in and have the correct role, render the protected content
    return children;
};

// Specific role-based route components
export const DoctorRoute = ({ children }) => (
    <ProtectedRoute requiredRole={["doctor", "doctors"]}>{children}</ProtectedRoute>
);

export const HospitalRoute = ({ children }) => (
    <ProtectedRoute requiredRole={["hospital", "hospitals"]}>{children}</ProtectedRoute>
);

export const AdminRoute = ({ children }) => (
    <ProtectedRoute requiredRole={["administrator", "administrators", "admin"]}>{children}</ProtectedRoute>
);
export const InsuranceRoute = ({ children }) => (
    <ProtectedRoute requiredRole={["insurance", "insurance_company", "insurance companies"]}>{children}</ProtectedRoute>
);
export default React.memo(ProtectedRoute);
